import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Route prefetching hook for faster navigation
 * Prefetches route chunks and critical data on hover/focus
 */
export function useRoutePrefetch() {
  const queryClient = useQueryClient();

  // Prefetch trial site data
  const prefetchTrialSite = useCallback(async (siteId: string) => {
    await queryClient.prefetchQuery({
      queryKey: ['trial-site', siteId],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('trial_sites_safe')
          .select(`*, organizations (id, name, type)`)
          .eq('id', siteId)
          .single();
        if (error) throw error;
        return data;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  }, [queryClient]);

  // Prefetch billing page data
  const prefetchBillingData = useCallback(async (siteId: string) => {
    // Prefetch invoices
    await queryClient.prefetchQuery({
      queryKey: ['site-invoices', siteId],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('site_invoices')
          .select('*')
          .eq('trial_site_id', siteId)
          .order('created_at', { ascending: false });
        if (error) throw error;
        return data || [];
      },
      staleTime: 2 * 60 * 1000, // 2 minutes
    });
  }, [queryClient]);

  // Prefetch route chunk
  const prefetchRouteChunk = useCallback((routeName: string) => {
    const chunkMap: Record<string, () => Promise<unknown>> = {
      'billing': () => import('@/pages/SiteActiveTrialsBilling'),
      'trial-site': () => import('@/pages/TrialSiteDetail'),
      'site-dashboard': () => import('@/pages/SiteDashboard'),
      'patient-dashboard': () => import('@/pages/PatientDashboard'),
    };

    const loader = chunkMap[routeName];
    if (loader) {
      loader().catch(() => {
        // Silently fail - this is just prefetching
      });
    }
  }, []);

  return {
    prefetchTrialSite,
    prefetchBillingData,
    prefetchRouteChunk,
  };
}

/**
 * Prefetch on link hover/focus
 * Usage: <Link {...usePrefetchOnHover(() => prefetchTrialSite(id))} to="/trial-site/...">
 */
export function usePrefetchOnHover(prefetchFn: () => void) {
  return {
    onMouseEnter: prefetchFn,
    onFocus: prefetchFn,
  };
}

/**
 * Auto-prefetch critical routes on idle
 */
export function useIdlePrefetch(routes: Array<{ name: string; prefetch: () => void }>) {
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const handle = requestIdleCallback(() => {
        routes.forEach(route => {
          route.prefetch();
        });
      }, { timeout: 3000 });

      return () => cancelIdleCallback(handle);
    } else {
      // Fallback for browsers without requestIdleCallback
      const timeout = setTimeout(() => {
        routes.forEach(route => {
          route.prefetch();
        });
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [routes]);
}
